import { useMemo, useState } from 'react';
import NextLink from 'next/link';
import useAuth from 'hooks/useAuth';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import { isMaster } from '../../../utils/perfil';
import { Num } from '../../../components/gps';
import { manualItems, manualScreens } from '../../../sections/dashboard/novidades/novidadesData';
import TituloTela from '../../../components/gps/TituloTela';

/* ============================================================================
   Manual - visual novo (shadcn + Tailwind).

   Reescrita no lugar: tela de LEITURA, sem chamada de API. O conteudo vem de
   sections/dashboard/novidades/novidadesData.ts, que nao foi tocado.

   TODOS os campos de `ManualScreenItem` continuam sendo exibidos: description,
   purpose, usage, highlights, featureChips, actionGroups, visualGuide e preview.
   Nada do conteudo ficou de fora.

   A REGRA DE PERMISSAO FOI PRESERVADA: as telas `regionais` e `regionais-acessos`
   so aparecem para MASTER (antes era a flag versaoDesenvolvedor).

   DUAS MUDANCAS DE ESTRUTURA:

   1. INDICE NO TOPO E CONTEUDO POR SELECAO, em vez de todos os cartoes abertos um
      embaixo do outro. Sao ~15 telas documentadas, cada uma com highlights,
      grupos de acao, guia visual e preview - tudo aberto de uma vez dava uma
      pagina de varios metros, e achar "como faco X" virava rolagem.

   2. A COR DE ACENTO DE CADA TELA (`screen.accent`, que vem do dado) e usada
      apenas numa BARRA e no icone, via `style` inline - nao em fundo com
      gradiente. Cor vinda de dado nao passa pelo Tailwind, e pintar area grande
      com ela ignora o tema escuro.
   ============================================================================ */

ManualPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function ManualPage() {
  const { user } = useAuth();

  /* O QUE E DE MASTER NAO APARECE NO MANUAL DE QUEM NAO E (10/09/2026).

     A lista de ids continua aqui para as telas que dependem do perfil sem
     estarem marcadas no dado (regionais), e a marca `soMaster` cobre o resto -
     ela vive junto do conteudo, em novidadesData, porque a tela de Novidades
     precisa esconder exatamente o mesmo conjunto.

     Nao e cosmetico: o manual descrevia ferramenta que so o Master abre, e
     falava em "versao desenvolvedor". Quem le e nao tem o perfil aprende que
     existe um nivel acima e o que ele pode fazer - informacao que nao ajuda
     em nada quem esta lendo e que so serve a quem quer chegar la. */
  const telasFlag = ['regionais', 'regionais-acessos'];
  const telas = useMemo(
    () => manualScreens.filter((t) => isMaster(user) || (!telasFlag.includes(t.id) && !t.soMaster)),
    [user]
  );

  const [ativa, setAtiva] = useState<string>(telas[0]?.id ?? '');
  /* ORDEM INVERTIDA (19/08/2026, a pedido).
     A tela abria com nove cartoes de "como fazer X" - blocos de texto de tamanho
     desigual, um ao lado do outro - e o indice de telas, que e a parte facil de
     entender, ficava embaixo de tudo, fora da primeira dobra. Quem abre o manual
     esta procurando UMA tela; ler nove receitas antes de chegar ao indice e o
     caminho mais longo possivel.
     Agora sao duas abas e "Telas do sistema" e a que abre. As receitas continuam
     inteiras na segunda aba - nada de conteudo foi removido. */
  const [aba, setAba] = useState<'telas' | 'comoFazer'>('telas');
  const [busca, setBusca] = useState('');

  const filtradas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return telas;
    return telas.filter(
      (s) =>
        s.title.toLowerCase().includes(t) ||
        s.description.toLowerCase().includes(t) ||
        s.purpose.toLowerCase().includes(t)
    );
  }, [telas, busca]);

  const tela = telas.find((s) => s.id === ativa) ?? filtradas[0] ?? telas[0];

  return (
    <Page title="Manual">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-4">
          <TituloTela
            descricao={
              <>
Escolha a tela na lista para ver para que ela serve, como usar e o que ela mostra. As receitas
            passo a passo estão na aba <strong className="text-ink">Como fazer</strong>.
              </>
            }
          >
            Manual
          </TituloTela>
        </div>

        <div role="tablist" aria-label="Manual" className="mb-4 flex gap-1 overflow-x-auto border-b border-line">
          {[
            { id: 'telas' as const, rotulo: `Telas do sistema (${telas.length})` },
            { id: 'comoFazer' as const, rotulo: `Como fazer (${manualItems.length})` },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={aba === t.id}
              onClick={() => setAba(t.id)}
              className={
                aba === t.id
                  ? '-mb-px flex-none border-b-2 border-primary px-3 py-2 text-[12px] font-bold text-primary'
                  : '-mb-px flex-none border-b-2 border-transparent px-3 py-2 text-[12px] font-semibold text-muted-foreground hover:border-line hover:text-ink'
              }
            >
              {t.rotulo}
            </button>
          ))}
        </div>

        {/* Receitas passo a passo - `manualItems`. Segunda aba.
            `md:grid-cols-2` e nao 3: com tres colunas cada cartao ficava com ~34
            caracteres por linha e a receita virava uma coluna alta e estreita. */}
        {aba === 'comoFazer' && (
        <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2">
          {manualItems.map((item) => (
            <div key={item.id} className="rounded-lg border border-line bg-surface p-4 shadow-card">
              <h2 className="text-[12.5px] font-bold">{item.title}</h2>
              <p className="mt-1 text-[12px] text-muted-foreground">{item.description}</p>

              <ol className="mt-3 space-y-1.5">
                {item.steps.map((passo, i) => (
                  <li key={passo} className="flex gap-2 text-[12px]">
                    <Num className="mt-0.5 grid h-4 w-4 flex-none place-items-center rounded bg-soft text-[10px] font-bold text-muted-foreground">
                      {i + 1}
                    </Num>
                    <span className="text-muted-foreground">{passo}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
        )}

        {aba === 'telas' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
          {/* Indice */}
          <div className="lg:sticky lg:top-4 lg:self-start">
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar tela"
              className="mb-2 h-9 w-full rounded-lg border border-line bg-surface px-3 text-[12.5px] outline-none placeholder:text-faint focus:border-primary"
            />

            <div className="overflow-hidden rounded-lg border border-line bg-surface">
              {filtradas.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setAtiva(s.id)}
                  className={`flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left last:border-b-0 hover:bg-surface-2 ${
                    tela?.id === s.id ? 'bg-surface-2' : ''
                  }`}
                >
                  <span
                    className="h-6 w-1 flex-none rounded-full"
                    style={{ backgroundColor: tela?.id === s.id ? s.accent : 'var(--gps-border)' }}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-[12px] font-semibold">
                      {s.emoji ? `${s.emoji} ` : ''}
                      {s.title}
                    </span>
                  </span>
                </button>
              ))}
              {!filtradas.length && (
                <div className="px-3 py-4 text-center text-[12px] text-faint">Nada encontrado.</div>
              )}
            </div>
          </div>

          {/* Conteudo da tela selecionada */}
          {tela && (
            <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
              <div className="h-1 w-full" style={{ backgroundColor: tela.accent }} />

              <div className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-xl font-extrabold tracking-[-0.02em]">
                      {tela.emoji ? `${tela.emoji} ` : ''}
                      {tela.title}
                    </h2>
                    <p className="mt-1 max-w-[75ch] text-[12.5px] text-muted-foreground">{tela.description}</p>
                  </div>

                  {tela.path && (
                    <NextLink
                      href={tela.path}
                      className="h-9 flex-none rounded-lg bg-primary px-4 text-[12px] font-bold leading-9 text-primary-foreground"
                    >
                      Abrir a tela
                    </NextLink>
                  )}
                </div>

                {!!tela.featureChips?.length && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {tela.featureChips.map((chip) => (
                      <span
                        key={chip}
                        className="rounded-md border border-line px-2 py-0.5 text-[11px] text-muted-foreground"
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="rounded-lg border border-line bg-surface-2 p-3.5">
                    <span className="block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
                      Para que serve
                    </span>
                    <p className="mt-1 text-[12px] text-muted-foreground">{tela.purpose}</p>
                  </div>
                  <div className="rounded-lg border border-line bg-surface-2 p-3.5">
                    <span className="block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
                      Como usar
                    </span>
                    <p className="mt-1 text-[12px] text-muted-foreground">{tela.usage}</p>
                  </div>
                </div>

                {!!tela.highlights?.length && (
                  <div className="mt-4">
                    <span className="block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
                      Destaques
                    </span>
                    <ul className="mt-1.5 space-y-1">
                      {tela.highlights.map((h) => (
                        <li key={h} className="flex gap-2 text-[12px] text-muted-foreground">
                          <span className="flex-none" style={{ color: tela.accent }}>
                            •
                          </span>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!!tela.actionGroups?.length && (
                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    {tela.actionGroups.map((grupo) => (
                      <div key={grupo.title} className="rounded-lg border border-line p-3.5">
                        <span className="block text-[12px] font-bold">{grupo.title}</span>
                        <ul className="mt-1.5 space-y-1">
                          {grupo.items.map((i) => (
                            <li key={i} className="flex gap-2 text-[12px] text-muted-foreground">
                              <span className="flex-none text-faint">–</span>
                              <span>{i}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {tela.visualGuide && (
                  <div className="mt-4 rounded-lg border border-line bg-surface-2 p-3.5">
                    <span className="block text-[12px] font-bold">{tela.visualGuide.title}</span>
                    <p className="mt-1 text-[12px] text-muted-foreground">{tela.visualGuide.description}</p>
                    <ol className="mt-2 space-y-1.5">
                      {tela.visualGuide.steps.map((passo, i) => (
                        <li key={passo} className="flex gap-2 text-[12px]">
                          <Num className="mt-0.5 grid h-4 w-4 flex-none place-items-center rounded bg-surface text-[10px] font-bold text-muted-foreground">
                            {i + 1}
                          </Num>
                          <span className="text-muted-foreground">{passo}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Preview: mostra como a tela se parece - selo, titulo, metricas
                    e as colunas da tabela. */}
                {tela.preview && (
                  <div className="mt-4 overflow-hidden rounded-lg border border-line">
                    <div className="flex items-center gap-2 border-b border-line bg-surface-2 px-3.5 py-2">
                      <span
                        className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                        style={{ backgroundColor: tela.accent }}
                      >
                        {tela.preview.badge}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-[12px] font-bold">{tela.preview.title}</span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {tela.preview.subtitle}
                        </span>
                      </span>
                    </div>

                    {!!tela.preview.metrics?.length && (
                      <div className="flex flex-wrap gap-2 border-b border-line px-3.5 py-2.5">
                        {tela.preview.metrics.map((m) => (
                          <span
                            key={m}
                            className="rounded-md border border-line bg-surface-2 px-2 py-1 text-[11px] text-muted-foreground"
                          >
                            {m}
                          </span>
                        ))}
                      </div>
                    )}

                    {!!tela.preview.columns?.length && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 px-3.5 py-2.5 text-[10px] font-bold uppercase tracking-[0.07em] text-faint">
                        {tela.preview.columns.map((c) => (
                          <span key={c}>{c}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </Page>
  );
}
